import { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, ChevronDown, RotateCcw } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  Legend,
} from "recharts";
import Seo from "@/components/Seo";
import ToolPrimer from "@/components/ToolPrimer";

/* ---------------- Model ---------------- */
const BASELINE = {
  C0: 50,
  c: 0.8,
  I0: 100,
  b: 20,
  G: 100,
  T: 100,
  M: 1000,
  P: 2,
  k: 0.5,
  h: 40,
};

const Y_MIN = 0;
const Y_MAX = 3000;
const POINTS = 60;

function equilibrium(p) {
  const realMoney = p.M / p.P;
  const A = p.C0 - p.c * p.T + p.I0 + p.G;
  const Y = (A + (p.b / p.h) * realMoney) / ((1 - p.c) + (p.b * p.k) / p.h);
  const r = (p.k * Y - realMoney) / p.h;
  return { Y, r };
}

function isCurve(p) {
  const A = p.C0 - p.c * p.T + p.I0 + p.G;
  const step = (Y_MAX - Y_MIN) / (POINTS - 1);
  return Array.from({ length: POINTS }, (_, i) => {
    const Y = Y_MIN + i * step;
    return { Y, r: (A - (1 - p.c) * Y) / p.b };
  });
}

function lmCurve(p) {
  const realMoney = p.M / p.P;
  const step = (Y_MAX - Y_MIN) / (POINTS - 1);
  return Array.from({ length: POINTS }, (_, i) => {
    const Y = Y_MIN + i * step;
    return { Y, r: (p.k * Y - realMoney) / p.h };
  });
}

/* ---------------- Presets ---------------- */
const PRESETS = [
  { id: "baseline", label: "Baseline", params: BASELINE, note: "Reset every slider to defaults." },
  { id: "fiscal-exp", label: "Fiscal expansion", params: { ...BASELINE, G: 200 }, note: "Government spending doubles. Watch the IS curve shift right — output up, rates up. Crowding-out visible in the gap between ΔY here and the pure multiplier." },
  { id: "fiscal-cont", label: "Austerity", params: { ...BASELINE, G: 40, T: 140 }, note: "Cut spending, raise taxes. IS shifts left twice — output and rates both fall." },
  { id: "monetary-exp", label: "Monetary expansion", params: { ...BASELINE, M: 2000 }, note: "Central bank doubles money supply. LM shifts right — output up, rates down." },
  { id: "supply-shock", label: "Inflation shock", params: { ...BASELINE, P: 4 }, note: "Prices double. Real money supply halves, LM shifts left — output down, rates up. The stagflation move." },
  { id: "confidence", label: "'08 confidence collapse", params: { ...BASELINE, I0: 20, c: 0.65 }, note: "Investment appetite and marginal propensity to consume both collapse. IS shifts sharply left — a demand-driven recession." },
];

/* ---------------- Slider metadata ---------------- */
const MAIN_SLIDERS = [
  { key: "G", label: "Government spending", hint: "Fiscal — shifts IS", min: 0, max: 300, step: 1 },
  { key: "T", label: "Taxes", hint: "Fiscal — shifts IS opposite of G", min: 0, max: 300, step: 1 },
  { key: "M", label: "Money supply", hint: "Monetary — shifts LM", min: 100, max: 3000, step: 10 },
  { key: "P", label: "Price level", hint: "Real money supply = M/P", min: 0.5, max: 10, step: 0.1 },
  { key: "c", label: "Marginal propensity to consume", hint: "Multiplier strength", min: 0.1, max: 0.99, step: 0.01, pct: true },
  { key: "I0", label: "Autonomous investment", hint: "Animal spirits — shifts IS", min: 0, max: 300, step: 1 },
];

const ADV_SLIDERS = [
  { key: "C0", label: "Autonomous consumption", hint: "Consumption when income is zero", min: 0, max: 200, step: 1 },
  { key: "b", label: "Investment sensitivity to r", hint: "IS slope: higher b → flatter IS", min: 1, max: 100, step: 1 },
  { key: "k", label: "Money demand · income", hint: "How much income drives money demand", min: 0.1, max: 2, step: 0.05 },
  { key: "h", label: "Money demand · interest", hint: "LM slope: higher h → flatter LM", min: 5, max: 200, step: 1 },
];

/* ---------------- Component ---------------- */
export default function ISLM() {
  const [params, setParams] = useState(BASELINE);
  const [showBaseline, setShowBaseline] = useState(true);
  const [advOpen, setAdvOpen] = useState(false);
  const [activePreset, setActivePreset] = useState("baseline");
  const [presetNote, setPresetNote] = useState(PRESETS[0].note);

  const set = useCallback((key) => (val) => {
    setParams((p) => ({ ...p, [key]: val }));
    setActivePreset(null);
  }, []);

  const applyPreset = useCallback((preset) => {
    setParams(preset.params);
    setActivePreset(preset.id);
    setPresetNote(preset.note);
  }, []);

  const reset = () => applyPreset(PRESETS[0]);

  const { chartData, eq, baselineEq, deltaFromBaseline } = useMemo(() => {
    const currentIS = isCurve(params);
    const currentLM = lmCurve(params);
    const baseIS = isCurve(BASELINE);
    const baseLM = lmCurve(BASELINE);
    const chart = currentIS.map((pt, i) => ({
      Y: pt.Y,
      IS: pt.r,
      LM: currentLM[i].r,
      IS_base: baseIS[i].r,
      LM_base: baseLM[i].r,
    }));
    const eq = equilibrium(params);
    const baselineEq = equilibrium(BASELINE);
    const delta = {
      Y: eq.Y - baselineEq.Y,
      r: eq.r - baselineEq.r,
    };
    return { chartData: chart, eq, baselineEq, deltaFromBaseline: delta };
  }, [params]);

  const isBaseline = deltaFromBaseline.Y === 0 && deltaFromBaseline.r === 0;

  return (
    <main data-testid="islm-tool" className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
      <Seo
        title="IS–LM Simulator"
        description="The Hicks–Hansen model made tactile. Slide fiscal and monetary policy; watch the curves and the equilibrium shift. Built for macroeconomics students."
        path="/tools/is-lm"
      />
      <Link
        to="/tools"
        data-testid="islm-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-primary transition-colors mb-10"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> All tools
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-3">
            Financial tool · Macroeconomics · IS–LM
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.02] tracking-tighter">
            Two curves,
            <br />
            <span className="italic text-primary">one economy.</span>
          </h1>
        </div>
        <p className="max-w-md text-sm text-foreground/60 leading-relaxed">
          The Hicks–Hansen IS–LM model, moved by your fingertips. Watch fiscal and
          monetary policy shift the curves in real time — and remember what your
          professor wanted you to feel.
        </p>
      </div>

      {/* Presets bar */}
      <section
        data-testid="islm-presets"
        className="flex flex-wrap gap-2 mb-10 pb-8 border-b border-border/60"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50 w-full mb-3">
          Policy scenarios
        </div>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => applyPreset(p)}
            data-testid={`islm-preset-${p.id}`}
            className={`text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-colors duration-200 ${
              activePreset === p.id
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-foreground/70 hover:border-primary hover:text-primary"
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={reset}
          data-testid="islm-reset"
          className="ml-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/60 hover:text-primary transition-colors"
        >
          <RotateCcw size={13} strokeWidth={1.5} /> Reset
        </button>
      </section>

      {activePreset && activePreset !== "baseline" && (
        <div
          data-testid="islm-preset-note"
          className="mb-10 border-l-2 border-primary pl-6 py-2 max-w-3xl"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-primary mb-2">
            Scenario · {PRESETS.find(p => p.id === activePreset)?.label}
          </div>
          <p className="text-base text-foreground/80 leading-relaxed">{presetNote}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        {/* Inputs */}
        <section className="lg:col-span-4 space-y-6" data-testid="islm-inputs">
          <div className="border border-border rounded-sm p-6 bg-background">
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/70">
                Parameters
              </div>
              <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-foreground/60 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showBaseline}
                  onChange={(e) => setShowBaseline(e.target.checked)}
                  data-testid="islm-show-baseline"
                />
                Show baseline
              </label>
            </div>
            {MAIN_SLIDERS.map((s, i) => (
              <Field
                key={s.key}
                s={s}
                value={params[s.key]}
                onChange={set(s.key)}
                last={i === MAIN_SLIDERS.length - 1}
              />
            ))}
          </div>

          <div className="border border-border rounded-sm bg-background overflow-hidden">
            <button
              onClick={() => setAdvOpen((v) => !v)}
              data-testid="islm-advanced-toggle"
              className="w-full flex items-center justify-between px-6 py-4 text-xs uppercase tracking-[0.25em] text-foreground/70 hover:text-primary transition-colors"
            >
              Advanced · structural params
              <ChevronDown
                size={16}
                strokeWidth={1.5}
                className={`transition-transform duration-300 ${advOpen ? "rotate-180" : ""}`}
              />
            </button>
            {advOpen && (
              <div className="px-6 pb-6 pt-2 border-t border-border">
                {ADV_SLIDERS.map((s, i) => (
                  <Field
                    key={s.key}
                    s={s}
                    value={params[s.key]}
                    onChange={set(s.key)}
                    last={i === ADV_SLIDERS.length - 1}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Chart + equilibrium */}
        <section className="lg:col-span-8 space-y-6">
          {/* Chart */}
          <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="islm-chart">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">
                IS–LM · goods & money market equilibrium
              </div>
              <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-foreground/60">
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-0.5 bg-primary" /> IS (goods)
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-0.5 bg-accent" /> LM (money)
                </span>
                {showBaseline && !isBaseline && (
                  <span className="inline-flex items-center gap-2 opacity-60">
                    <span className="w-4 h-0.5 border-t-2 border-dashed border-foreground" /> Baseline
                  </span>
                )}
              </div>
            </div>
            <div className="h-[420px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis
                    dataKey="Y"
                    type="number"
                    domain={[Y_MIN, Y_MAX]}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    label={{ value: "Output / Income  Y", position: "insideBottom", offset: -4, fill: "hsl(var(--muted-foreground))", fontSize: 11, style: { textTransform: "uppercase", letterSpacing: "0.15em" } }}
                  />
                  <YAxis
                    domain={[-5, 15]}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${v.toFixed(1)}%`}
                    width={55}
                    label={{ value: "Interest rate  r", angle: -90, position: "insideLeft", offset: 12, fill: "hsl(var(--muted-foreground))", fontSize: 11, style: { textTransform: "uppercase", letterSpacing: "0.15em" } }}
                  />
                  <Tooltip content={<ChartTip />} />
                  {showBaseline && !isBaseline && (
                    <>
                      <Line
                        type="monotone"
                        dataKey="IS_base"
                        stroke="hsl(var(--foreground))"
                        strokeOpacity={0.25}
                        strokeWidth={1.25}
                        strokeDasharray="4 4"
                        dot={false}
                        isAnimationActive={false}
                        name="IS baseline"
                      />
                      <Line
                        type="monotone"
                        dataKey="LM_base"
                        stroke="hsl(var(--foreground))"
                        strokeOpacity={0.25}
                        strokeWidth={1.25}
                        strokeDasharray="4 4"
                        dot={false}
                        isAnimationActive={false}
                        name="LM baseline"
                      />
                    </>
                  )}
                  <Line
                    type="monotone"
                    dataKey="IS"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2.5}
                    dot={false}
                    isAnimationActive={false}
                    name="IS"
                  />
                  <Line
                    type="monotone"
                    dataKey="LM"
                    stroke="hsl(var(--accent))"
                    strokeWidth={2.5}
                    dot={false}
                    isAnimationActive={false}
                    name="LM"
                  />
                  {/* Equilibrium markers */}
                  {showBaseline && !isBaseline && (
                    <ReferenceDot
                      x={baselineEq.Y}
                      y={baselineEq.r}
                      r={5}
                      fill="hsl(var(--background))"
                      stroke="hsl(var(--foreground))"
                      strokeOpacity={0.5}
                      strokeWidth={1.5}
                      isFront
                    />
                  )}
                  <ReferenceDot
                    x={eq.Y}
                    y={eq.r}
                    r={7}
                    fill="hsl(var(--destructive))"
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                    isFront
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Equilibrium summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BigStat
              label="Equilibrium output (Y*)"
              value={eq.Y.toFixed(1)}
              delta={deltaFromBaseline.Y}
              deltaFmt={(v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}`}
              testId="islm-eq-y"
            />
            <BigStat
              label="Equilibrium interest rate (r*)"
              value={`${eq.r.toFixed(2)}%`}
              delta={deltaFromBaseline.r}
              deltaFmt={(v) => `${v > 0 ? "+" : ""}${v.toFixed(2)} pts`}
              testId="islm-eq-r"
              accent
            />
          </div>

          {/* How to read */}
          <div className="border border-border rounded-sm p-6 md:p-8 bg-secondary/40" data-testid="islm-legend-panel">
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50 mb-4">
              How to read this
            </div>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-foreground/75 leading-relaxed">
              <div>
                <div className="font-serif text-lg text-primary mb-1">IS curve</div>
                <p>
                  Every (Y, r) that clears the <em>goods market</em>: planned spending equals income. Fiscal
                  policy (G, T) and animal spirits (I<sub>0</sub>, c) shift it.
                </p>
              </div>
              <div>
                <div className="font-serif text-lg text-accent mb-1">LM curve</div>
                <p>
                  Every (Y, r) that clears the <em>money market</em>: money demand equals real money
                  supply. Monetary policy (M) and prices (P) shift it.
                </p>
              </div>
              <div className="md:col-span-2 pt-4 border-t border-border/60">
                Their intersection is the only pair where <em>both</em> markets clear at once — the
                equilibrium of the short-run economy. Move a slider and watch the shift; the dashed
                lines show where you started so you can see the story unfold.
              </div>
            </div>
          </div>
        </section>
      </div>

      <ToolPrimer
        title="Two markets, one equilibrium."
        sections={[
          {
            heading: "What the model says",
            body: "The IS-LM model, first sketched by John Hicks in 1937, compresses Keynes into a single diagram. The IS curve traces every combination of income (Y) and interest rate (r) at which the goods market clears — where planned spending equals output. The LM curve traces every combination where the money market clears — where money demand equals real money supply. The economy sits where the two curves cross: the only point at which both markets are in equilibrium at the same time.",
          },
          {
            heading: "How to read the shifts",
            body: "Fiscal policy — a change in government spending G or taxes T — shifts the IS curve. Expansionary fiscal policy pushes IS right, raising both income and the interest rate. Monetary policy — a change in nominal money supply M or the price level P — shifts LM. Expansionary monetary policy pushes LM right, raising income and lowering the rate. The dashed grey lines show the previous equilibrium so you can see the direction and size of the move at a glance.",
          },
          {
            heading: "Where the intuition comes from",
            body: "Sensitivity parameters matter. A steep IS (low interest sensitivity of investment) means monetary policy is weak; a flat LM (high interest sensitivity of money demand — the classic liquidity trap) means the same thing from the other side. Play with these values and the standard textbook results — crowding out, the policy mix, the liquidity trap — appear as visual consequences rather than definitions to memorise.",
          },
        ]}
        caveat="IS-LM is a short-run, closed-economy, fixed-price teaching model. It is invaluable for building intuition and useless as a forecast — real economies have expectations, open capital accounts, sticky wages and a central bank that reacts. Use it to think, not to predict."
      />
    </main>
  );
}

/* ---------------- Sub-components ---------------- */
function Field({ s, value, onChange, last }) {
  const display = s.pct
    ? value.toFixed(2)
    : Number.isInteger(s.step) ? value.toFixed(0) : value.toFixed(2);
  return (
    <div className={last ? "" : "mb-6"}>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-[11px] uppercase tracking-[0.18em] text-foreground/80">
          {s.label} <span className="text-foreground/40 lowercase tracking-normal">— {s.key}</span>
        </label>
        <input
          type="number"
          value={value}
          min={s.min}
          max={s.max}
          step={s.step}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!isNaN(v)) onChange(Math.max(s.min, Math.min(s.max, v)));
          }}
          data-testid={`islm-${s.key}-input`}
          className="w-20 text-right bg-transparent font-serif text-base tabular-nums focus:outline-none border-b border-border focus:border-primary transition-colors"
        />
      </div>
      <div className="text-[10px] text-foreground/45 mb-2 italic">{s.hint}</div>
      <Slider
        value={[value]}
        min={s.min}
        max={s.max}
        step={s.step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={`islm-${s.key}-slider`}
      />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-foreground/40 tabular-nums">
        {display}
      </div>
    </div>
  );
}

function BigStat({ label, value, delta, deltaFmt, testId, accent }) {
  const hasDelta = Math.abs(delta) > 1e-6;
  const positive = delta > 0;
  return (
    <div className="border border-border rounded-sm p-8 bg-background" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50">{label}</div>
      <div className={`font-serif text-5xl md:text-6xl mt-3 tracking-tighter ${accent ? "text-primary" : ""}`}>
        {value}
      </div>
      {hasDelta ? (
        <div
          className={`mt-3 text-xs uppercase tracking-[0.2em] ${
            positive ? "text-primary" : "text-destructive"
          }`}
        >
          {deltaFmt(delta)} vs baseline
        </div>
      ) : (
        <div className="mt-3 text-xs uppercase tracking-[0.2em] text-foreground/40">
          At baseline
        </div>
      )}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const is = payload.find((p) => p.dataKey === "IS")?.value;
  const lm = payload.find((p) => p.dataKey === "LM")?.value;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-1">
        At Y = {Number(label).toFixed(0)}
      </div>
      <div className="text-xs space-y-0.5">
        {is !== undefined && (
          <div className="text-primary">
            IS · r = {is.toFixed(2)}%
          </div>
        )}
        {lm !== undefined && (
          <div className="text-accent">
            LM · r = {lm.toFixed(2)}%
          </div>
        )}
      </div>
    </div>
  );
}
